/**
 * Krok UXR1 — co se poskláda do bloku „Dnes mám" a do věty stavu nad dnem
 * (mockup `01-dnesek.html`). Ciste funkce bez Reactu a bez textu: obrazovka
 * dodava „ted" i popisky, tady je jen poradi a pocty. Diky tomu jde logika
 * otestovat bez prohlizece a bez posunu casu.
 *
 * Krok AS2 — presunuto z `apps/web-admin/src/lib/dnesek-model.ts` a
 * `apps/web-admin/src/lib/waiting-for-me.ts` do platformoveho balicku (SSOT,
 * docs/platform/TESTOVANI.md §2): `reos.today` v `apps/mcp-gateway` pocita
 * „na tobě / dnes mám / zakázky v pohybu" STEJNOU funkci jako obrazovka
 * Dnešek, ne kopii. Oba puvodni soubory ve web-adminu jsou jen re-export.
 *
 * Krok MRGB1 — pri napojeni linky B na `integrace` sem prisla CELA logika
 * Dneška ve stavu po UXR5 a TAB1 (kontextove akce, `splitEntryTitle`,
 * `overdueTaskCount`, `splitOnYou`), ne jen to, co stihlo AS2 odnest.
 */
import { officeDayStart } from '@reos/config';
/**
 * Krok UXR5 bod 1 — NADPIS A DRUHY RADEK POLOZKY.
 *
 * Predloha ma nahore kratky nazev („Prohlídka") a pod nim adresu
 * („Poděbradova 141/44, Lysá nad Labem"). Kalendar maklere ale posila nazev
 * uz slozeny („Prohlídka — Poděbradova 141/44"), takze bez rozdeleni by byl
 * druhy radek prazdny a nazev dlouhy (presne to je na snimku UXR3).
 *
 * Deli se na POMLCCE se dvema mezerami — to je tvar, jaky nazvy skutecne
 * maji (`crm_calendar_events.title` na preview). Kdyz v nazvu neni, polozka
 * zustava cela nahore a druhy radek nese misto, kdyz ho udalost ma.
 */
export function splitEntryTitle(title, location) {
    const cut = title.indexOf(' — ');
    const head = cut < 0 ? title : title.slice(0, cut).trim();
    const tail = cut < 0 ? null : title.slice(cut + 3).trim();
    // Misto z udalosti je presnejsi nez utrzek nazvu (nese i obec), proto ma
    // prednost; `tail` je zaloha pro udalosti bez vyplneneho mista.
    const detail = location !== null && location !== '' ? location : tail;
    return { title: head, detail: detail === '' ? null : detail };
}
/** Kontextova akce polozky — viz `TodayActionKind`. */
function meetingAction(eventType, isFirstViewing) {
    switch (eventType) {
        // Predloha: prvni prohlidka dne vede na navigaci, kazda dalsi uz jen na
        // detail — druhou prohlidku tehoz dne makler nenavigujes dvakrat.
        case 'viewing':
            return isFirstViewing ? 'navigate' : 'detail';
        case 'handover':
            return 'protocol';
        default:
            return 'open';
    }
}
/**
 * Schuzky a ukoly do jednoho seznamu, serazene podle casu; co cas nema,
 * jde na konec (mockup: „do večera — Objednat list vlastnictví").
 */
export function buildTodayEntries(meetings, tasks) {
    const entries = [
        ...meetings.map((meeting) => {
            const text = splitEntryTitle(meeting.title, meeting.location ?? null);
            return {
                id: `meeting-${meeting.id}`,
                kind: 'meeting',
                at: meeting.startsAt,
                title: text.title,
                detail: text.detail,
                overdue: false,
                done: false,
                eventType: meeting.eventType ?? null,
                caseId: meeting.caseId ?? null,
                color: meeting.color ?? null,
                // Prepise se nize, az bude znamé poradi (prvni prohlidka dne).
                action: 'open',
            };
        }),
        ...tasks.map((task) => ({
            id: `task-${task.id}`,
            kind: 'task',
            at: task.dueAt ?? null,
            title: task.title,
            detail: task.detail ?? null,
            overdue: task.overdue === true,
            done: task.status === 'done',
            eventType: null,
            caseId: null,
            color: null,
            action: 'done',
        })),
    ];
    const sorted = entries.sort((a, b) => {
        if (a.at === null && b.at === null)
            return 0;
        if (a.at === null)
            return 1;
        if (b.at === null)
            return -1;
        // Porovnava se OKAMZIK, ne retezec: kalendar posila casy s posunem
        // (`+02:00`) a ukoly v UTC (`Z`), takze textove by 11:00 SELC vyslo
        // pozdeji nez 16:00 SELC (nalez pri zivem overeni UXR1).
        return new Date(a.at).getTime() - new Date(b.at).getTime();
    });
    // Akce az nad SERAZENYM seznamem: „prvni prohlidka" je prvni v case, ne
    // prvni, jak ji vratilo API.
    let viewingSeen = false;
    return sorted.map((entry) => {
        if (entry.kind === 'task')
            return entry;
        const first = entry.eventType === 'viewing' && !viewingSeen;
        if (entry.eventType === 'viewing')
            viewingSeen = true;
        return { ...entry, action: meetingAction(entry.eventType, first) };
    });
}
/**
 * Krok FIX6 bod 4: endpoint `/v1/crm/tasks/today` posila i ukoly s VCEREJSIM
 * terminem. UXR1 je nechal v „Dnes mám" a jen k nim dopsal den (aby holy cas
 * nelhal) — nalez ze ziveho overeni 2. 10. ale rika, ze predloha
 * `01-dnesek.html` ma v „Dnes mám" POUZE dnesek a veci po termínu jinde
 * („po termínu zvlášť nebo v Čeká na mě"). Rozdeleni je ciste a testovatelne
 * tady, aby obrazovka nemusela filtrovat dvakrat.
 */
export function splitTodayEntries(entries) {
    return {
        today: entries.filter((entry) => !entry.overdue),
        overdue: entries.filter((entry) => entry.overdue),
    };
}
/**
 * Krok TAB1 bod 1 — ODZNAK U POLOŽKY MENU „Úkoly“.
 *
 * Zadání: „odznak = po termínu (STEJNÁ FUNKCE jako Dnešek → Na tobě, ne
 * vlastní počet)“. Menu i Dnešek proto počítají jedním výrazem nad týmiž
 * daty (`GET /v1/crm/tasks/today`, společný klíč react-query v
 * `lib/waiting-for-me.ts`): úkoly po termínu jsou ty, které `splitTodayEntries`
 * odsune z programu do „Na tobě“.
 *
 * Schůzky se sem nepředávají schválně — kalendářní událost `overdue` nikdy
 * není (viz `buildTodayEntries`), takže by do počtu stejně nepřispěla, a
 * odznak nemá na menu čekat na druhé volání API.
 */
export function overdueTaskEntries(tasks) {
    return splitTodayEntries(buildTodayEntries([], tasks)).overdue;
}
export function overdueTaskCount(tasks) {
    return overdueTaskEntries(tasks).length;
}
/**
 * Podtitulek dlazdice „Na tobě": „smlouva k podpisu · faktura po splatnosti".
 * Nic nehori → jedna veta o tom, ze je klid (nikdy prazdny radek).
 *
 * Krok UXR5 bod 4: oddelovac je ` · ` a veta nema tecku — presne tak ji ma
 * predloha `Main.dc.html`. Do UXR5 to byl vycet carkami zakonceny teckou,
 * ktery se na dlazdici lamal do tri radku (snimek UXR3).
 */
export function buildLede(counts, texts) {
    const listings = counts.listings ?? 0;
    const parts = [
        counts.contracts > 0 ? texts.contracts(counts.contracts) : null,
        counts.invoices > 0 ? texts.invoices(counts.invoices) : null,
        counts.tasks > 0 ? texts.tasks(counts.tasks) : null,
        counts.emails > 0 ? texts.emails(counts.emails) : null,
        listings > 0 ? texts.listings(listings) : null,
        counts.approvals > 0 ? texts.approvals(counts.approvals) : null,
        counts.toResolve > 0 ? texts.toResolve(counts.toResolve) : null,
    ].filter((part) => part !== null);
    if (parts.length === 0)
        return texts.allClear;
    return parts.join(' · ');
}
export function emailCardsOnYou(cards) {
    return cards.filter((card) => card.status === 'open');
}
/**
 * Krok UXR5 bod 2 — KOLIK KARET „Na tobě" UNESE OBRAZOVKA.
 *
 * Predloha `Main.dc.html` ma v bloku NEJVYS DVE karty a pod nimi jeden radek
 * „… další N věcí — zobrazit". Nalez R.B. 3. 10. 08:41: ctyri karty pod
 * sebou vytlacily blok „Zakázky v pohybu" uplne pod okraj okna 1440×900.
 */
export const ON_YOU_CARDS = 2;
/**
 * Prvnich `max` polozek jako karty, zbytek jako pocet.
 *
 * Poradi urcuje VOLAJICI (`routes/dnesek.tsx`: podpis smlouvy → faktura po
 * splatnosti → zbytek) — tahle funkce o vyznamu polozek nic nevi, jen krati.
 * Dlazdice nahore pocita CELY seznam (`rows.length`), ne jen karty: cislo a
 * seznam jsou jedna data (zadani UXR5 bod 4).
 */
export function splitOnYou(rows, max = ON_YOU_CARDS) {
    return { cards: rows.slice(0, max), rest: Math.max(0, rows.length - max) };
}
/** Krok UXR3b bod 3 — kolik dni dozadu je zakazka jeste „v pohybu" (predloha `Main.dc.html`: „změna za posledních 7 dní"). */
export const CASES_IN_MOTION_WINDOW_DAYS = 7;
/**
 * Zakazka je „v pohybu", kdyz se zmenila (`updatedAt`) v posledních
 * `windowDays` dnech. Pocita se OKAMZIK (rolling window), ne kalendarni
 * den — stejny duvod jako u razeni v `buildTodayEntries`: hranice podle
 * datumu by se posouvala s casovym pasmem, rozdil dvou okamziku ne.
 */
export function isCaseInMotion(updatedAt, now, windowDays = CASES_IN_MOTION_WINDOW_DAYS) {
    const windowMs = windowDays * 24 * 60 * 60 * 1000;
    return now.getTime() - new Date(updatedAt).getTime() <= windowMs;
}
/** Krok AI9 bod 2 — po kolika dnech beze zmeny je zakazka „bez pohybu" pro manazera kancelare. */
export const CASE_STALLED_WINDOW_DAYS = 14;
/**
 * Zakazky BEZ POHYBU — manazerský přehled (AI9 bod 2). Opak `isCaseInMotion`:
 * stejne okamzikove srovnani updatedAt vs. `now`, jen jiny prah (14 dní, ne
 * 7) a jiny smer (chybejici zmena, ne pritomna) — zadna druha implementace
 * „kolik dni je zakazka bez zmeny".
 */
export function staleCases(cases, now, windowDays = CASE_STALLED_WINDOW_DAYS) {
    return cases.filter((item) => !isCaseInMotion(item.updatedAt, now, windowDays));
}
/**
 * Krok UXR3 — KDE V PROGRAMU LEZI CARA „teď" (predloha `Main.dc.html`).
 *
 * Vraci index prvni polozky, ktera je JESTE PRED sebou; cara se kresli nad
 * ni. Kdyz uz dnes nic nezbyva, je to delka seznamu (cara pod poslednim
 * radkem) — a kdyz naopak jeste nic neproslo, je to 0. Polozka bez casu
 * („do večera") se povazuje za budouci, protoze presne to znamena.
 *
 * Proc tady a ne v komponente: je to jediny vypocet, ktery zavisi na „ted",
 * a bez cisté funkce by se nedal otestovat bez posouvani systemoveho casu.
 */
export function nowLineIndex(entries, now) {
    const ms = now.getTime();
    const index = entries.findIndex((entry) => entry.at === null || new Date(entry.at).getTime() >= ms);
    return index < 0 ? entries.length : index;
}
/**
 * Krok UXR1 — „Čeká na mě" na JEDNOM miste (puvodne `lib/waiting-for-me.ts`).
 * Pocty u polozek menu (Smlouvy, Fakturace) a u zvonku v hlavicce musi rikat
 * totez, co blok „Čeká na mě" na Dnesku — definice „po splatnosti" je jedna
 * funkce, genericka nad tvarem faktury (web-admin i gateway maji jiny DTO typ
 * ze stejneho API).
 */
export function startOfToday(now) {
    /*
     * Krok UXP4a bod 5 (nalez P2-18): hranice dne je `Europe/Prague`, ne
     * lokalni cas prohlizece. Driv tu stalo `new Date(now.getFullYear(), …)`,
     * zatimco server rozhodoval o dnesnich UKOLECH podle 00:00 UTC — na Dnesku
     * pak vedle sebe stala schuzka dnesniho dne a ukol vcerejsiho. Hranice je
     * ted JEDNA pro server i prohlizec (`officeDayStart` v `@reos/config`).
     */
    return officeDayStart(now);
}
/** Vystavene faktury s terminem splatnosti pred dneskem. */
export function overdueInvoices(invoices, now) {
    const today = startOfToday(now);
    return invoices.filter((invoice) => new Date(invoice.dueDate) < today);
}
export function countOverdueInvoices(invoices, now) {
    return overdueInvoices(invoices, now).length;
}
/** Krok D7 bod 3: kolik dni pred koncem zaplaceneho mesice se o nem mluvi. */
export const MEMBERSHIP_ENDING_SOON_DAYS = 7;
/**
 * Krok NAP11 (A25) — AKTUALNI OBDOBI CLENSTVI (tarifu), bez ohledu na to,
 * jak daleko je jeho konec.
 *
 * Tohle je ta cast `membershipEndingSoon`, kterou potrebuje i obrazovka
 * Nastaveni → Tarif („aktualni tarif a do kdy plati"). Vyrezana jako SSOT:
 * dve kopie pravidla „obdobi konci NEJPOZDEJSI platnosti nezruseneho naroku"
 * by se rozesly pri prvni zmene (napr. dalsi `unusableReason`).
 *
 * `null` = kancelar zadny tarif nema (nikdy nezaplatila, nebo byly vsechny
 * naroky zruseny vracenim penez).
 */
export function currentMembership(entitlements) {
    const periods = entitlements.filter((item) => item.kind === 'MEMBERSHIP' && item.unusableReason !== 'REVOKED');
    if (periods.length === 0)
        return null;
    return periods.reduce((latest, item) => new Date(item.expiresAt).getTime() > new Date(latest.expiresAt).getTime() ? item : latest);
}
export function membershipEndingSoon(entitlements, now, withinDays = MEMBERSHIP_ENDING_SOON_DAYS) {
    const last = currentMembership(entitlements);
    if (last === null)
        return null;
    const msLeft = new Date(last.expiresAt).getTime() - now.getTime();
    if (msLeft <= 0)
        return null;
    const daysLeft = Math.ceil(msLeft / (24 * 60 * 60 * 1000));
    if (daysLeft > withinDays)
        return null;
    return { expiresAt: last.expiresAt, daysLeft, sourceProductCode: last.sourceProductCode };
}
export * from './checks.js';
export * from './broker-briefing.js';
//# sourceMappingURL=index.js.map